class FormatMapper {
  readonly source: RegExp;
  readonly destination: string;

  constructor(source: RegExp, destination: string) {
    this.source = source;
    this.destination = destination;
  }

  map(format: string): string {
    const groups = format.match(this.source);
    return format.replace(groups[2], this.destination);
  }
}

const yearMappers = [
  //yyyy, y -> Y (case insensitive y to uppercase Y)
  new FormatMapper(/(^[^y]*)(y|y{4}?)([^y]*$)/i, "Y"),
  // yy -> y
  new FormatMapper(/(^[^y]*)(y{2}?)([^y]*$)/, "y"),
];

const dayMappers = [
  // dd -> d
  new FormatMapper(/(^[^d]*)(d{2}?)([^d]*$)/, "d"),
  // d  -> j
  new FormatMapper(/(^[^d]*)(d)([^d]*$)/, "j"),
  // E, EE, EEE  -> D
  new FormatMapper(/(^[^E]*)(E|E{2}?|E{3}?)([^E]*$)/, "D"),
  // EEEE -> l (small L)
  new FormatMapper(/(^[^E]*)(E{4})([^E]*$)/, "l"),
  // e -> w
  new FormatMapper(/(^[^e]*)(e)([^e]*$)/, "w"),
];

const monthMappers = [
  // M -> n
  new FormatMapper(/(^[^M]*)(M)([^M]*$)/, "n"),
  // MM -> m (case insensitive mm to lowercase m)
  new FormatMapper(/(^[^M]*)(M{2}?)([^M]*$)/i, "m"),
  // MMM -> M
  new FormatMapper(/(^[^M]*)(M{3}?)([^M]*$)/, "M"),
  // MMMM -> F
  new FormatMapper(/(^[^M]*)(M{4}?)([^M]*$)/, "F"),
];

class FormatMatcher {
  static matchYear(format: string): FormatMapper {
    return yearMappers.find((mapper) => mapper.source.test(format));
  }

  static matchDay(format: string): FormatMapper {
    return dayMappers.find((mapper) => mapper.source.test(format));
  }

  static matchMonth(format: string): FormatMapper {
    return monthMappers.find((mapper) => mapper.source.test(format));
  }
}

class FormatConverter {
  private yearMapper: FormatMapper;
  private dayMapper: FormatMapper;
  private monthMapper: FormatMapper;

  constructor(
    yearMapper: FormatMapper,
    dayMapper: FormatMapper,
    monthMapper: FormatMapper
  ) {
    this.yearMapper = yearMapper;
    this.dayMapper = dayMapper;
    this.monthMapper = monthMapper;
  }

  convert(format: string): string {
    let mapped = this.yearMapper.map(format);
    mapped = this.dayMapper.map(mapped);
    mapped = this.monthMapper.map(mapped);
    return mapped;
  }
}

export class DateFormatService {
  convertDateFormat(format: string): string {
    const formatConverter = this.buildConverterFrom(format);
    return formatConverter.convert(format);
  }

  private buildConverterFrom(format): FormatConverter {
    return new FormatConverter(
      FormatMatcher.matchYear(format),
      FormatMatcher.matchDay(format),
      FormatMatcher.matchMonth(format)
    );
  }
}

export default new DateFormatService();
